import { useState, useEffect, useMemo } from "react";
import Modal, { ModalSize } from "./Modal";
import PrimaryButton from "../PrimaryButton";
import { useModalStore, ModalType } from "@/store/useModalStore";
import {
  AUDIO_VOICE_MAP,
  toVoicePreviewOption,
  getVoicesForLanguage,
  getBetaVoicesForLanguage,
  getLocalizedName,
  getNamePronunciationKey,
  type AudioVoice,
  type AudioVoiceFormData,
} from "@/constants/audioVoice";
import { CHARACTER_MAP } from "@/constants/character";
import { CHARACTER_ICONS } from "@/assets/icons/characters";
import { useForm } from "@/features/tutor/context/FormContext";
import { recordPreferredVoice } from "@/features/tutor/context/preferredVoice";
import { TutorMode } from "@/constants/mode";
import VoicePreview, { VisualMode } from "../voice/VoicePreview";
import { useVoicePlayer } from "@/hooks/useVoicePlayer";
import { useAuthStore } from "@/store/useAuthStore";
import { useNamePronunciationAudio } from "@/hooks/useNamePronunciationAudio";
import api from "@/utils/axios-utils";
import { Play } from "lucide-react";
import SpeakingPersonIcon from "@/components/icons/SpeakingPersonIcon";
import "./lesson-entry.css";

const ChangeVoiceModal = ({
  isOpen,
  onClose,
  returnModalType = ModalType.SESSION_START,
  returnModalProps,
}: {
  isOpen: boolean;
  onClose: () => void;
  returnModalType?: ModalType;
  returnModalProps?: Record<string, unknown>;
}) => {
  const [searchValue, setSearchValue] = useState("");
  const { formData, updateField, mode } = useForm();
  const { openModal, closeModal } = useModalStore();
  const { user } = useAuthStore();
  const showBetaVoices = Boolean(user?.config?.enableBetaTtsProviders);
  const languageVoices = useMemo(() => {
    const lang = formData.language?.value || "en";
    // Beta voices list in full here — picking a voice is independent of name
    // training. The Name & Voice screen keeps its trainable-only filter.
    return showBetaVoices
      ? [...getVoicesForLanguage(lang), ...getBetaVoicesForLanguage(lang)]
      : getVoicesForLanguage(lang);
  }, [formData.language?.value, showBetaVoices]);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>(
    formData.voice?.voiceId || languageVoices[0].id,
  );
  const [selectedSpeed, setSelectedSpeed] = useState<string>(
    formData.voice?.speed || "medium",
  );

  const currentLanguage = formData.language?.value || "en";
  const selectedVoice = AUDIO_VOICE_MAP[selectedVoiceId] || languageVoices[0];
  const voicePreviewOption = toVoicePreviewOption(
    selectedVoice,
    currentLanguage,
  );

  const {
    progress: voiceProgress,
    handleProgress,
    handlePlayStateChange,
    handlePlayPause,
    voicePreviewRef,
  } = useVoicePlayer(voicePreviewOption);

  useEffect(() => {
    if (isOpen) {
      const currentId = formData.voice?.voiceId;
      const isValid =
        currentId && languageVoices.some((v) => v.id === currentId);
      setSelectedVoiceId(isValid ? currentId : languageVoices[0].id);
      setSelectedSpeed(formData.voice?.speed || "medium");
    }
  }, [isOpen, languageVoices]);

  // The whole bundle goes back untouched. Rebuilding it field by field is what
  // used to drop the caller's assessment props on the way home.
  const getReturnModal = () => ({
    type: returnModalType,
    props: returnModalProps || {},
  });

  const [namePronunciations, setNamePronunciations] = useState<
    Record<string, { spelling: string; trainedAt: number }>
  >({});

  useEffect(() => {
    if (!isOpen) return;
    setNamePronunciations({});
    void api
      .get<Record<string, { surfaceText: string; updatedAtMs: number }>>(
        "/name-pronunciation/entries",
      )
      .then((res) => {
        setNamePronunciations(
          Object.fromEntries(
            Object.entries(res.data).map(([key, entry]) => [
              key,
              {
                spelling: entry.surfaceText,
                trainedAt: entry.updatedAtMs ?? 0,
              },
            ]),
          ),
        );
      })
      .catch((err) => {
        console.error(
          "[ChangeVoiceModal] Failed to load pronunciation entries:",
          err,
        );
      });
  }, [isOpen]);

  const filteredVoices = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    const base = q
      ? languageVoices.filter(
          (v) =>
            getLocalizedName(v, currentLanguage).toLowerCase().includes(q) ||
            v.name.toLowerCase().includes(q) ||
            v.accent.toLowerCase().includes(q),
        )
      : languageVoices;
    // Trained voices (for current language) float to top, sorted by most recently trained
    const trained = base
      .filter(
        (v) => namePronunciations[getNamePronunciationKey(v, currentLanguage)],
      )
      .sort((a, b) => {
        const ta =
          namePronunciations[getNamePronunciationKey(a, currentLanguage)]
            ?.trainedAt ?? 0;
        const tb =
          namePronunciations[getNamePronunciationKey(b, currentLanguage)]
            ?.trainedAt ?? 0;
        return tb - ta;
      });
    const untrained = base.filter(
      (v) => !namePronunciations[getNamePronunciationKey(v, currentLanguage)],
    );
    return [...trained, ...untrained];
  }, [searchValue, languageVoices, namePronunciations, currentLanguage]);

  const speedOptions = useMemo(() => {
    const opts = selectedVoice.speedOptions;
    return user?.config?.disableFastSpeed
      ? opts.filter((o) => o.value !== "fast")
      : opts;
  }, [selectedVoice, user?.config?.disableFastSpeed]);

  const handleSelectVoice = (voice: AudioVoice) => {
    stopPronunciation();
    setSelectedVoiceId(voice.id);
  };

  const handleConfirm = () => {
    stopPronunciation();
    const voiceData: AudioVoiceFormData = {
      voiceId: selectedVoiceId,
      speed: selectedSpeed,
    };
    updateField("voice", voiceData);
    // First point the choice is real: card clicks above are local state only.
    if (mode !== TutorMode.PREVIEW) {
      recordPreferredVoice(
        selectedVoiceId,
        formData.language.value,
        selectedSpeed,
      );
    }
    const nextModal = getReturnModal();
    closeModal();
    openModal(nextModal.type, nextModal.props);
  };

  const {
    playCurrent,
    isPlayingCurrent,
    stop: stopPronunciation,
  } = useNamePronunciationAudio();

  // Stop pronunciation playback whenever the modal closes.
  useEffect(() => {
    if (!isOpen) stopPronunciation();
  }, [isOpen]);

  const character = CHARACTER_MAP[formData.character?.characterId];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={false}
      size={ModalSize.wide}
      className="et-lesson-entry-modal et-entry-picker et-entry-picker-wide p-0"
    >
      <div className="flex h-full min-h-0 w-full flex-col">
        {/* Topbar */}
        <div className="flex flex-shrink-0 flex-row items-center gap-3 px-6 pt-5 pb-2">
          <button
            type="button"
            onClick={() => {
              stopPronunciation();
              const nextModal = getReturnModal();
              openModal(nextModal.type, nextModal.props);
            }}
            className="flex-none w-[40px] h-[40px] rounded-[6px] bg-[#edf0e5] text-[#28513e] border-none cursor-pointer flex items-center justify-center transition-colors duration-150 hover:bg-[#dfe7d7]"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-[18px] h-[18px]"
            >
              <path d="M10 18 L4 12 L10 6" />
              <path d="M4 12 H20" />
            </svg>
          </button>
          <div className="flex-1 flex items-center gap-2.5 bg-[#fdfbf6] border border-[#d9d7ca] rounded-[6px] px-3.5 py-2.5 transition-colors duration-150 focus-within:border-[#28513e]">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              className="w-[18px] h-[18px] text-[#28543D] flex-none"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20 L16 16" />
            </svg>
            <input
              type="text"
              placeholder="Search for a voice"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="flex-1 border-none outline-none bg-transparent font-inherit text-[14.5px] text-[#28513e] placeholder:text-[#7b857c]"
            />
          </div>
        </div>

        {/* Step hint */}
        <div className="flex items-center gap-2 px-6 pb-2 text-[13.5px] text-[#637264]">
          <span className="flex-none inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-[#EEF0E3] text-[#28543D]">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-3 h-3"
            >
              <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
              <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
              <path d="M12 18v4" />
            </svg>
          </span>
          <span>Pick any voice — pairs with any character and language.</span>
        </div>

        {/* Voice grid */}
        <div className="min-h-[280px] flex-1 overflow-y-auto px-6 pb-4">
          {filteredVoices.length === 0 ? (
            <p className="py-10 text-center text-[14px] text-[#687368]">
              No voices match &quot;{searchValue}&quot;.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {filteredVoices.map((voice) => {
                const isSelected = voice.id === selectedVoiceId;
                const displayName = getLocalizedName(voice, currentLanguage);
                const isTrained = Boolean(
                  namePronunciations[
                    getNamePronunciationKey(voice, currentLanguage)
                  ],
                );
                return (
                  <button
                    key={voice.id}
                    onClick={() => handleSelectVoice(voice)}
                    className={`relative flex items-center gap-3 rounded-[6px] border p-3.5 text-left transition-all ${
                      isSelected
                        ? "border-[#28543D] bg-[#EEF0E3] shadow-[0_0_0_3px_rgba(40,84,61,0.1)]"
                        : "border-[#d9d7ca] bg-[#fdfbf6] hover:border-[#c8cebd] hover:shadow-sm"
                    }`}
                  >
                    {/* Radio */}
                    <span
                      className={`flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full border-[1.5px] ${
                        isSelected
                          ? "border-[#28543D] border-2"
                          : "border-[#c8cebd]"
                      }`}
                    >
                      {isSelected && (
                        <span className="h-[11px] w-[11px] rounded-full bg-[#28543D]" />
                      )}
                    </span>

                    {/* Meta */}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[15px] font-bold text-[#28513e]">
                          {displayName}
                        </span>
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                            voice.gender === "f"
                              ? "bg-[#FDE7EF] text-[#8A3457]"
                              : "bg-[#E4EEF8] text-[#2A4F7A]"
                          }`}
                        >
                          {voice.gender === "f" ? "Feminine" : "Masculine"}
                        </span>
                        {isTrained && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const saved =
                                namePronunciations[
                                  getNamePronunciationKey(
                                    voice,
                                    currentLanguage,
                                  )
                                ];
                              if (saved && !isPlayingCurrent) {
                                voicePreviewRef.current?.pause();
                                playCurrent(
                                  saved.spelling,
                                  voice,
                                  currentLanguage,
                                );
                              }
                            }}
                            className="inline-flex items-center justify-center w-[26px] h-[26px] flex-shrink-0 rounded-full bg-[#EEF0E3] text-[#28543D] border-none cursor-pointer p-0 transition-colors hover:bg-[#E2E9DC]"
                            title="Says your name right — click to hear it"
                            aria-label="Play saved name pronunciation"
                          >
                            <SpeakingPersonIcon />
                          </button>
                        )}
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {[voice.accent, voice.tone, voice.range].map(
                          (tag, i) => (
                            <span
                              key={i}
                              className="rounded-full bg-[#edf0e5] px-2 py-0.5 text-[11px] text-[#52685a]"
                            >
                              {tag}
                            </span>
                          ),
                        )}
                      </div>
                    </div>

                    {/* Preview play button */}
                    <span
                      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-[1.5px] transition-colors ${
                        isSelected
                          ? "border-[#28543D] bg-[#28543D] text-white"
                          : "border-[#28543D] bg-transparent text-[#28543D] hover:bg-[#28543D] hover:text-white"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        stopPronunciation();
                        handleSelectVoice(voice);
                        // Defer playback until after React re-renders with the
                        // newly selected voice — handlePlayPause reads selectedVoiceId
                        // which is updated asynchronously by handleSelectVoice.
                        setTimeout(() => handlePlayPause(), 50);
                      }}
                    >
                      <Play className="h-3.5 w-3.5" fill="currentColor" />
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Bottom player bar */}
        <div className="flex flex-shrink-0 flex-col border-t border-[#ded8cc]">
          <div className="flex items-center gap-3 px-6 py-3">
            <button
              onClick={() => {
                stopPronunciation();
                handlePlayPause();
              }}
              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#28513e] text-[#fbf8f1] shadow-md transition-colors hover:bg-[#173e2c]"
            >
              <Play className="h-4 w-4 ml-0.5" fill="currentColor" />
            </button>
            {/* Character mini-avatar */}
            {CHARACTER_ICONS[character?.id] ? (
              <img
                src={CHARACTER_ICONS[character.id]}
                alt=""
                className="h-[30px] w-[30px] flex-shrink-0 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full bg-[#edf0e5] text-xs font-bold text-[#28513e]">
                {character?.name?.[0] || "?"}
              </div>
            )}
            <span className="min-w-0 truncate text-[15px] font-bold text-[#28513e]">
              {getLocalizedName(selectedVoice, currentLanguage)}
            </span>
            <span className="ml-auto flex-shrink-0" />
            <span className="text-[14px] text-[#637264]">Speed :</span>
            <div className="relative inline-flex items-center">
              <select
                value={selectedSpeed}
                onChange={(e) => setSelectedSpeed(e.target.value)}
                className="appearance-none bg-[#edf0e5] text-[#28513e] border-none rounded-[6px] py-[7px] pl-3 pr-8 font-inherit font-semibold text-[13.5px] cursor-pointer"
              >
                {speedOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="pointer-events-none absolute right-2 w-3 h-3 text-[#28543D]"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </div>
          {/* Progress bar */}
          <div className="relative mx-6 h-[3px] rounded-full bg-[#ded8cc]">
            <div
              className="absolute left-0 top-0 h-full rounded-full bg-[#F5C842] transition-all duration-100"
              style={{ width: `${voiceProgress}%` }}
            />
          </div>
        </div>

        {/* Hidden voice preview for audio */}
        <div className="hidden">
          <VoicePreview
            ref={voicePreviewRef}
            voice={voicePreviewOption}
            visualMode={VisualMode.DEFAULT}
            onProgress={handleProgress}
            onPlayStateChange={handlePlayStateChange}
            onSpeedChange={(speed) => setSelectedSpeed(speed)}
            defaultSpeed={selectedSpeed}
            speedOptions={speedOptions}
          />
        </div>

        {/* CTA */}
        <div className="flex flex-shrink-0 justify-center px-6 pt-4 pb-5">
          <PrimaryButton onClick={handleConfirm} className="!min-w-[260px]">
            Choose this Voice & Speed
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default ChangeVoiceModal;
